import { z } from 'zod';
import { HARVEST_PHASES } from '../constants/phases.constants';
import { CULTURES } from '../constants/cultures.constants';
import { CROP_STAGES } from '../constants/cropStages.constants';
import { NUTRIENTS, NUTRIENT_NAMES } from '../constants/nutrients.constants';

const FIXED_PARCEL_TOTAL = 100;
const FLOATING_POINT_TOLERANCE = 1e-9;

const hasAtMostOneDecimalPlace = (value: number) =>
	Math.abs(Math.round(value * 10) - value * 10) < 1e-6;

const nutrientArraySchema = z
	.array(
		z.number({ error: 'Preencha todos os valores dos nutrientes' })
			.min(0, 'Valor não pode ser negativo')
			.max(100, 'Valor não pode ser maior que 100')
			.refine(hasAtMostOneDecimalPlace, 'Valor deve ter no máximo uma casa decimal')
	)
	.length(12, 'Deve conter exatamente 12 valores (um por mês)');

const phaseArraySchema = z
	.array(z.enum(HARVEST_PHASES, { error: 'Selecione uma fase válida para cada mês' }))
	.length(12, 'Deve conter exatamente 12 fases (uma por mês)');

export const parcelDataSchema = z
	.object({
		phase: phaseArraySchema,
		N: nutrientArraySchema,
		P: nutrientArraySchema,
		K: nutrientArraySchema,
		Ca: nutrientArraySchema,
		Mg: nutrientArraySchema,
		S: nutrientArraySchema,
		B: nutrientArraySchema,
		Cu: nutrientArraySchema,
		Fe: nutrientArraySchema,
		Mn: nutrientArraySchema,
		Zn: nutrientArraySchema,
	})
	.superRefine((data, ctx) => {
		NUTRIENTS.forEach((nutrient) => {
			const values = data[nutrient];
			if (values.some((value) => Number.isNaN(value))) return;

			const total = values.reduce((sum, value) => sum + value, 0);
			if (Math.abs(total - FIXED_PARCEL_TOTAL) > FLOATING_POINT_TOLERANCE) {
				ctx.addIssue({
					code: 'custom',
					path: [nutrient],
					message: `O total de ${NUTRIENT_NAMES[nutrient]} deve somar 100% nos 12 meses (atual: ${total.toFixed(1)}%).`,
				});
			}
		});
	});

export const dynamicParcelSchema = z.array(z.object({
	phase: z.enum(HARVEST_PHASES),
	weeks: z.number({ error: 'Informe as semanas de cada fase' }).int('Semanas devem ser inteiras').min(1).max(52),
	...Object.fromEntries(NUTRIENTS.map((nutrient) => [nutrient,
		z.number({ error: `Informe o percentual de ${nutrient}` }).min(0).max(100)
			.refine(hasAtMostOneDecimalPlace, 'Valor deve ter no máximo uma casa decimal'),
	])) as Record<typeof NUTRIENTS[number], z.ZodNumber | z.ZodType<number>>,
})).length(HARVEST_PHASES.length).superRefine((entries, ctx) => {
	if (entries.some((entry, index) => entry.phase !== HARVEST_PHASES[index])) {
		ctx.addIssue({ code: 'custom', message: 'Informe as oito fases em ordem fenológica' });
	}
	if (entries.reduce((sum, entry) => sum + entry.weeks, 0) !== 52) {
		ctx.addIssue({ code: 'custom', path: ['weeks'], message: 'O parcelamento deve totalizar 52 semanas' });
	}
	NUTRIENTS.forEach((nutrient) => {
		const total = entries.reduce((sum, entry) => sum + entry[nutrient], 0);
		if (Math.abs(total - 100) > FLOATING_POINT_TOLERANCE) {
			ctx.addIssue({ code: 'custom', path: [nutrient], message: `O total de ${NUTRIENT_NAMES[nutrient]} deve somar 100% (atual: ${total.toFixed(1)}%).` });
		}
	});
});

const parcellingFieldsSchema = z.object({
	name: z
		.string()
		.trim()
		.min(3, 'Identificação deve ter no mínimo 3 caracteres')
		.max(100, 'Identificação deve ter no máximo 100 caracteres'),

	culture: z.enum(CULTURES, { error: 'Selecione uma cultura válida' }),

	cropStage: z.enum(CROP_STAGES, { error: 'Selecione um estágio de cultura válido' }),

	note: z
		.string()
		.max(500, 'Observação deve ter no máximo 500 caracteres')
		.optional()
		.or(z.literal('')),

	bindingUserIds: z.array(z.string()).default([]),

	farmIds: z.array(z.string()).default([]),

	isPublic: z.boolean().default(false),

	typeParcelling: z.enum(['fixed', 'dynamic']).default('fixed'),
	maturationCycle: z.enum(['early', 'medium', 'late']).optional(),
	dynamicParcel: z.array(z.custom<import('../types').DynamicParcelEntry>()).default([]),
	parcels: z.custom<import('../types').ParcelData>(),
});

export const parcellingSchema = parcellingFieldsSchema.superRefine((data, ctx) => {
	const result = data.typeParcelling === 'dynamic'
		? dynamicParcelSchema.safeParse(data.dynamicParcel)
		: parcelDataSchema.safeParse(data.parcels);
	if (!result.success) result.error.issues.forEach((issue) => ctx.addIssue({
		...issue, path: [data.typeParcelling === 'dynamic' ? 'dynamicParcel' : 'parcels', ...issue.path],
	}));
	if (data.typeParcelling === 'dynamic' && !data.maturationCycle) {
		ctx.addIssue({ code: 'custom', path: ['maturationCycle'], message: 'Selecione o ciclo de maturação' });
	}
});

export const createParcellingSchema = parcellingSchema;

export const updateParcellingSchema = parcellingFieldsSchema.partial().extend({
	_id: z.string().min(1, 'ID é obrigatório'),
	dynamicParcel: z.array(z.custom<import('../types').DynamicParcelEntry>()).optional(),
	typeParcelling: z.enum(['fixed', 'dynamic']).optional(),
	bindingUserIds: z.array(z.string()).optional(),
	farmIds: z.array(z.string()).optional(),
	isPublic: z.boolean().optional(),
}).superRefine((data, ctx) => {
	if (data.parcels !== undefined) {
		const result = parcelDataSchema.safeParse(data.parcels);
		if (!result.success) result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['parcels', ...issue.path] }));
	}
	if (data.dynamicParcel !== undefined) {
		const result = dynamicParcelSchema.safeParse(data.dynamicParcel);
		if (!result.success) result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['dynamicParcel', ...issue.path] }));
	}
});

export type ParcellingFormData = z.infer<typeof parcellingSchema>;
export type CreateParcellingData = z.infer<typeof createParcellingSchema>;
export type UpdateParcellingData = z.infer<typeof updateParcellingSchema>;
